import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Play, Pause, Loader } from 'lucide-react';
import '../styles/StudentDoubts.css';

const StudentDoubts = () => {
  const [doubts, setDoubts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const audioElementsRef = useRef({});

  useEffect(() => {
    fetchDoubts();
    return () => {
      // Cleanup function to pause all audio and remove event listeners
      Object.values(audioElementsRef.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
          audio.removeEventListener('ended', audio.onEnded);
          audio.removeEventListener('error', audio.onError);
        }
      });
      audioElementsRef.current = {};
    };
  }, []);

  const fetchDoubts = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://quiz-server-ghre.onrender.com/api/doubts');
      if (!response.ok) throw new Error('Failed to fetch doubts');
      const data = await response.json();
      
      const sortedDoubts = data.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      
      setDoubts(sortedDoubts);
      setError(null);

      // Initialize audio elements after fetching doubts
      sortedDoubts.forEach(doubt => {
        const audio = new Audio(doubt.audioUrl);
        
        // Store the event handler functions on the audio element itself
        audio.onEnded = () => {
          console.log('Audio ended:', doubt.audioUrl);
          setCurrentlyPlaying(null);
        };
        
        audio.onError = () => {
          console.error('Audio error:', doubt.audioUrl);
          setError('Failed to play audio');
          setCurrentlyPlaying(null);
        };

        audio.addEventListener('ended', audio.onEnded);
        audio.addEventListener('error', audio.onError);
        
        audioElementsRef.current[doubt.audioUrl] = audio;
      });

    } catch (err) {
      console.error('Error fetching doubts:', err);
      setError('Failed to load doubts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (publicId) => {
    if (!window.confirm('Are you sure you want to delete this doubt?')) return;

    try {
      console.log('Attempting to delete:', publicId);
      const response = await fetch(`https://quiz-server-ghre.onrender.com/api/doubts/${encodeURIComponent(publicId)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete doubt');
      }
      
      // Stop and cleanup audio if it was playing
      const doubtToDelete = doubts.find(d => d.id === publicId);
      if (doubtToDelete && audioElementsRef.current[doubtToDelete.audioUrl]) {
        const audio = audioElementsRef.current[doubtToDelete.audioUrl];
        audio.pause();
        audio.removeEventListener('ended', audio.onEnded);
        audio.removeEventListener('error', audio.onError);
        delete audioElementsRef.current[doubtToDelete.audioUrl];
      }

      setDoubts(doubts.filter(doubt => doubt.id !== publicId));
      setError({ type: 'success', message: 'Doubt deleted successfully' });
      
      if (currentlyPlaying === doubtToDelete?.audioUrl) {
        setCurrentlyPlaying(null);
      }
    } catch (err) {
      console.error('Error deleting doubt:', err);
      setError(`Failed to delete doubt: ${err.message}`);
    }
  };

  const handlePlayPause = (audioUrl) => {
    const audio = audioElementsRef.current[audioUrl];
    if (!audio) return;

    if (currentlyPlaying === audioUrl) {
      // Pause current audio
      audio.pause();
      setCurrentlyPlaying(null);
    } else {
      // Pause any playing audio
      if (currentlyPlaying && audioElementsRef.current[currentlyPlaying]) {
        audioElementsRef.current[currentlyPlaying].pause();
      }
      // Play new audio
      audio.play().catch(error => {
        console.error('Error playing audio:', error);
        setError('Failed to play audio');
        setCurrentlyPlaying(null);
      });
      setCurrentlyPlaying(audioUrl);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="doubts-loading">
        <Loader className="spin" size={24} />
        <p>Loading doubts...</p>
      </div>
    );
  }

  return (
    <div className="student-doubts-container">
      <div className="doubts-header">
        <h1>Student Doubts</h1>
      </div>

      {error && (
        <div className={`message ${error.type === 'success' ? 'success-message' : 'error-message'}`}>
          {typeof error === 'string' ? error : error.message}
        </div>
      )}

      {doubts.length === 0 ? (
        <p className="no-doubts">No doubts found</p>
      ) : (
        <div className="doubts-grid">
          {doubts.map((doubt) => (
            <div key={doubt.id} className="doubt-card">
              <div className="doubt-header">
                <div className="doubt-info">
                  <span className="username">{doubt.username}</span>
                  <span className="date">{formatDate(doubt.createdAt)}</span>
                </div>
              </div>

              <div className="audio-controls">
                <button
                  className="play-button"
                  onClick={() => handlePlayPause(doubt.audioUrl)}
                  aria-label={currentlyPlaying === doubt.audioUrl ? "Pause" : "Play"}
                >
                  {currentlyPlaying === doubt.audioUrl ? (
                    <Pause size={20} />
                  ) : (
                    <Play size={20} />
                  )}
                </button>
              </div>

              <div className="doubt-actions">
                <button
                  className="delete-button"
                  onClick={() => handleDelete(doubt.public_id || doubt.id)}
                  aria-label="Delete doubt"
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentDoubts; 
